import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useUploadSingleFileMutation } from "../../../../Services/ProductService";
import {
  Box,
  Button,
  TextField,
  Grid as Grid2,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  colors,
  Divider,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import {
  useAddAndUpdateCategoryMutation,
  useLazyGetCategoryListQuery,
} from "../../../../Services/CategoryService";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../Redux/Slices/NotificationSlice";
import { useConfirmation } from "../../../../Components/ConfirmationDialog";
import Utils from "../../../../Helpers/Utils";

interface AddCategoryProps {
  open: boolean;
  onClose: () => void;
  updateData?: any;
}

interface CategoryFormInputs {
  parentCategoryName: string;
  categoryName: string;
  description: string;
  isActive: boolean;
  _id?: string;
  icon?: string;
}

const AddCategory: React.FC<AddCategoryProps> = ({
  open,
  onClose,
  updateData = {},
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const { requestConfirmation } = useConfirmation();
  const [
    createCategory,
    { isLoading, isSuccess, error: createError, isError },
  ] = useAddAndUpdateCategoryMutation();
  const [getCategoryListTrigger, { isFetching }] =
    useLazyGetCategoryListQuery();
  const [triggerSingleFileUpload] = useUploadSingleFileMutation();
  const [iconError, setIconError] = useState<string>("");

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty },
  } = useForm<CategoryFormInputs>({
    defaultValues: {
      categoryName: "",
      description: "",
      parentCategoryName: "",
      isActive: true,
    },
    mode: "onChange",
    resolver: (values) => {
      const errors: any = {};

      // Parent Category name validation
      if (!values.parentCategoryName) {
        errors.parentCategoryName = {
          type: "required",
          message: "Parent Category name is required",
        };
      }

      // Category name validation
      if (!values.categoryName) {
        errors.categoryName = {
          type: "required",
          message: "Category name is required",
        };
      } else if (values.categoryName.length < 3) {
        errors.categoryName = {
          type: "minLength",
          message: "Category name must be at least 3 characters",
        };
      } else if (values.categoryName.length > 50) {
        errors.categoryName = {
          type: "maxLength",
          message: "Category name cannot exceed 50 characters",
        };
      }

      // Description validation
      if (!values.description) {
        errors.description = {
          type: "required",
          message: "Description is required",
        };
      } else if (values.description.length < 10) {
        errors.description = {
          type: "minLength",
          message: "Description must be at least 10 characters",
        };
      } else if (values.description.length > 500) {
        errors.description = {
          type: "maxLength",
          message: "Description cannot exceed 500 characters",
        };
      }

      return {
        values,
        errors,
      };
    },
  });

  React.useEffect(() => {
    if (updateData?._id) {
      setValue("categoryName", updateData.categoryName);
      setValue("parentCategoryName", updateData.parentCategoryName);
      setValue("description", updateData.description);
      setValue("isActive", updateData.isActive ?? true);
      setValue("_id", updateData._id);
      setValue("icon", updateData.icon);
      setPreviewUrl(updateData.icon || "");
    } else {
      reset();
      setSelectedFile(null);
      setPreviewUrl("");
    }
  }, [updateData, setValue]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const allowedFormats = ["image/png", "image/jpeg", "image/jpg"];

      if (!allowedFormats.includes(file.type)) {
        dispatch(
          showSnackbar({
            message: "Only PNG, JPEG, and JPG formats are allowed",
            severity: "error",
          }),
        );
        return;
      }

      setSelectedFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (!updateData?._id && !previewUrl && getValues("categoryName") !== "") {
      // console.log(previewUrl, 'previewUrl', updateData?._id);
      validateIcon();
    }
  }, [previewUrl, open]);

  const validateIcon = (): boolean => {
    if (!selectedFile && !updateData?.icon) {
      setIconError("Category icon is required");
      return false;
    }
    setIconError("");
    return true;
  };

  const onSubmit = async (data: CategoryFormInputs) => {
    if (!validateIcon()) return;

    try {
      // let iconUrl: any = data.icon;
      let iconUrl;

      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);

        const uploadResponse = await triggerSingleFileUpload(formData).unwrap();
        if (uploadResponse?.data?.url) {
          iconUrl = uploadResponse?.data?.url;
        }
      }

      if (updateData?._id) {
        // Update request
        await createCategory({
          id: updateData._id,
          data: {
            parentCategoryName: data.parentCategoryName,
            categoryName: data.categoryName.trim(),
            description: data.description.trim(),
            isActive: data.isActive,
            icon: iconUrl || updateData.icon,
          },
        }).unwrap();
        await getCategoryListTrigger();
      } else {
        // Create request
        await createCategory({
          parentCategoryName: data.parentCategoryName,
          categoryName: data.categoryName.trim(),
          description: data.description.trim(),
          isActive: data.isActive,
          icon: iconUrl,
        }).unwrap();
        await getCategoryListTrigger();
      }

      dispatch(
        showSnackbar({
          message: updateData?._id
            ? "Category updated successfully!"
            : "Category created successfully!",
          severity: "success",
        }),
      );

      onClose();
      reset();
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err?.data?.message || "Failed to create category",
          severity: "error",
        }),
      );
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      requestConfirmation({
        title: "Unsaved changes detected",
        message: `You have unsaved changes. Are you sure you want to leave?`,
        onConfirm: async () => {
          onClose();
        },
        onCancel: () => {},
      });
    } else {
      onClose();
    }
  };

  return (
    <Box
      sx={{ display: "flex", flex: 1, flexDirection: "column", height: "100%" }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 3 }}>
          <Grid2 container spacing={3}>
            {/* Parent Category Name */}
            <Grid2 size={12}>
              <Controller
                name="parentCategoryName"
                control={control}
                rules={{
                  required: "Parent Category is required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth size="small" error={!!error}>
                    <InputLabel>Parent Category</InputLabel>
                    <Select
                      {...field}
                      label="Parent Category"
                      sx={{ minHeight: 43 }}
                    >
                      {/*
                        The parents the customer site navigates by. Hardcoded
                        because a parent is not a record in this system -- it
                        is the string in each category's parentCategoryName.

                        International Top Up was left out of this list, on the
                        reasoning that the customer-facing menu is built from
                        each product's "Tops up which country" field rather
                        than from where its category was filed -- so offering
                        it here implied a placement choice with no effect.

                        That was half true and it blocked real work. The
                        MENU is indeed built from the destination field. But
                        International Top Up is also a genuine top-level
                        category holding sub-categories and 16 products, and
                        the Add Product form already offers those categories
                        to choose from. So the two forms disagreed: products
                        could be filed under it, and no new category could be
                        created under it. An admin hit that wall with nothing
                        on screen to explain it.

                        Listed now, with the caption below saying what it does
                        and does not do, rather than being silently absent.

                        Travel stays absent for a different reason: those
                        products come from FlixBus directly, so there is no
                        category for an admin to create under it.
                      */}
                      <MenuItem value="Gift Cards">Gift Cards</MenuItem>
                      <MenuItem value="Phone Refill">Phone Refill</MenuItem>
                      <MenuItem value="Gaming">Gaming</MenuItem>
                      <MenuItem value="International Top Up">
                        International Top Up
                      </MenuItem>
                    </Select>
                    {error ? (
                      <FormHelperText>{error.message}</FormHelperText>
                    ) : (
                      <FormHelperText>
                        Filing a product under International Top Up does not
                        by itself put it in that menu &mdash; set the
                        product&apos;s &ldquo;Tops up which country&rdquo; to
                        a country other than Germany for that.
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid2>

            {/* Category Name */}
            <Grid2 size={12}>
              <Controller
                name="categoryName"
                control={control}
                rules={{
                  required: "Category name is required",
                  minLength: {
                    value: 3,
                    message: "Category name must be at least 3 characters",
                  },
                  maxLength: {
                    value: 15,
                    message: "Category name cannot exceed 15 characters",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    onInput={(e: any) => Utils.onlyText(e)}
                    {...field}
                    fullWidth
                    size="small"
                    label="Category Name"
                    placeholder="Enter category name"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>

            {/* Category Description */}
            <Grid2 size={12}>
              <Controller
                name="description"
                control={control}
                rules={{
                  required: "Description is required",
                  minLength: {
                    value: 10,
                    message: "Description must be at least 10 characters",
                  },
                  maxLength: {
                    value: 500,
                    message: "Description cannot exceed 500 characters",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    onInput={(e: any) => Utils.onlyText(e)}
                    rows={8}
                    size="small"
                    className="custom-mui-textarea"
                    label="Description"
                    placeholder="Enter category description"
                    error={!!error}
                    helperText={error?.message}
                    sx={{ height: 150 }}
                  />
                )}
              />
            </Grid2>

            {/* Category Icon Upload */}
            <Grid2 size={12}>
              <Box sx={{ mb: 2, mt: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="category-icon-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="category-icon-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{ height: "45px" }}
                  >
                    Upload Category Icon
                  </Button>
                </label>
                {previewUrl && (
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <img
                      src={previewUrl}
                      alt="Category Icon Preview"
                      style={{
                        maxWidth: "100px",
                        maxHeight: "100px",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
              </Box>
              {iconError && !isLoading && (
                <FormHelperText
                  error
                  sx={{ textAlign: "left", marginTop: "-15px" }}
                >
                  {iconError}
                </FormHelperText>
              )}
            </Grid2>

            {/* Status Dropdown */}
            <Grid2 size={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={value}
                      onChange={(e) => onChange(e.target.value === "true")}
                      label="Status"
                      sx={{
                        height: "45px",
                        fontSize: "12px",
                        "& .MuiSelect-select": {
                          paddingTop: "10px",
                          paddingBottom: "10px",
                        },
                      }}
                    >
                      <MenuItem value={"true"} sx={{ fontSize: "12px" }}>
                        Active
                      </MenuItem>
                      <MenuItem value={"false"} sx={{ fontSize: "12px" }}>
                        Inactive
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid2>
          </Grid2>
        </Box>

        {/* Footer */}
        <Box
          id="footer"
          sx={{
            mt: "auto",
            display: "flex",
            gap: 2,
            py: 1,
            px: 2,
            backgroundColor: colors.common.white,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={handleCancel}
            fullWidth
            variant="outlined"
            color="error"
          >
            Cancel
          </Button>

          <Divider orientation="vertical" flexItem />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            color="primary"
            disabled={isLoading || isFetching}
            onClick={() => validateIcon()}
          >
            {isLoading || isFetching ? (
              <CircularProgress size={16} />
            ) : updateData?._id ? (
              "Update Category"
            ) : (
              "Add Category"
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AddCategory;
